import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "./api";
import { Avatar, CommandPalette, IconButton, SidebarNav, Text, ToastProvider, useSidebarNav, type IconName } from "./ds";
import { Enlace, navegar, useUbicacion } from "./router";
import { SesionProvider, useSesion } from "./sesion";
import type { ListaLeads } from "./tipos";
import { Cola } from "./pantallas/Cola";
import { Configuracion } from "./pantallas/Configuracion";
import { Detalle } from "./pantallas/Detalle";
import { Leads } from "./pantallas/Leads";
import { Mensajes } from "./pantallas/Mensajes";
import { NoEncontrada } from "./pantallas/NoEncontrada";
import { Pipeline } from "./pantallas/Pipeline";
import { Plantillas } from "./pantallas/Plantillas";
import { Tablero } from "./pantallas/Tablero";
import { Zonas } from "./pantallas/Zonas";

export function App() {
  return (
    <ToastProvider>
      <SesionProvider>
        <Chasis />
      </SesionProvider>
    </ToastProvider>
  );
}

const ITEMS: { href: string; label: string; icon: IconName }[] = [
  { href: "/", label: "Tablero", icon: "layout-dashboard" },
  { href: "/cola", label: "Cola de WhatsApp", icon: "whatsapp" },
  { href: "/pipeline", label: "Pipeline", icon: "kanban" },
  { href: "/leads", label: "Leads", icon: "building-2" },
  { href: "/mensajes", label: "Mensajes", icon: "inbox" },
  { href: "/zonas", label: "Zonas", icon: "map" },
  { href: "/plantillas", label: "Plantillas", icon: "file-text" },
];
const AJUSTES = [{ href: "/configuracion", label: "Configuración", icon: "settings-2" as IconName }];

function Chasis() {
  const { pathname } = useUbicacion();
  const { sesion } = useSesion();
  const [buscador, setBuscador] = useState(false);

  const activo =
    [...ITEMS, ...AJUSTES]
      .map((i) => i.href)
      .filter((h) => pathname === h || (h !== "/" && pathname.startsWith(`${h}/`)))
      .sort((a, b) => b.length - a.length)[0] ?? "";

  const aItem = (i: { href: string; label: string; icon: IconName }) => ({
    value: i.href,
    href: i.href,
    label: i.label,
    icon: i.icon,
    // Sólo si hay algo pendiente: un "0" fijo entrena a no mirarlo.
    count: i.href === "/cola" && sesion?.pendientes ? sesion.pendientes : undefined,
  });

  useEffect(() => {
    const titulo = [...ITEMS, ...AJUSTES].find((i) => i.href === activo)?.label;
    if (titulo && pathname === activo) document.title = `${titulo} · Prospección Klavia`;
  }, [activo, pathname]);

  return (
    <div className="chasis">
      <SidebarNav
        as={Enlace}
        brand="Klavia"
        items={ITEMS.map(aItem)}
        sections={[{ id: "ajustes", label: "Ajustes", pinned: true, items: AJUSTES.map(aItem) }]}
        search={{ placeholder: "Buscar inmobiliaria", onOpen: () => setBuscador(true) }}
        value={activo}
        footer={<Cuenta nombre={sesion?.usuario ?? ""} firma={sesion?.firma ?? ""} />}
        defaultCollapsed={leerContraida()}
        onCollapsedChange={(c) => {
          try {
            localStorage.setItem("barra-contraida", c ? "1" : "0");
          } catch {
            /* sin almacenamiento: la barra arranca expandida */
          }
        }}
      />
      <Buscador abierto={buscador} onAbierto={setBuscador} />
      <main className="principal">
        <Pantalla pathname={pathname} />
      </main>
    </div>
  );
}

function leerContraida(): boolean {
  try {
    return localStorage.getItem("barra-contraida") === "1";
  } catch {
    return false;
  }
}

function Pantalla({ pathname }: { pathname: string }) {
  if (pathname === "/") return <Tablero />;
  if (pathname === "/cola") return <Cola />;
  if (pathname === "/pipeline") return <Pipeline />;
  if (pathname === "/leads") return <Leads />;
  const lead = pathname.match(/^\/leads\/([0-9a-f-]{36})$/i);
  if (lead) return <Detalle key={lead[1]} id={lead[1]!} />;
  if (pathname === "/mensajes") return <Mensajes />;
  if (pathname === "/zonas") return <Zonas />;
  if (pathname === "/plantillas") return <Plantillas />;
  if (pathname === "/configuracion") return <Configuracion />;
  return <NoEncontrada />;
}

function Cuenta({ nombre, firma }: { nombre: string; firma: string }) {
  const { collapsed } = useSidebarNav();
  const { salir } = useSesion();
  const local = nombre === "local";
  const visible = local ? "Acceso local" : firma || nombre;
  if (!nombre) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--s-5)", padding: "var(--s-2) var(--s-4)" }} title={collapsed ? visible : undefined}>
      <Avatar nombre={visible} tone="solid" size="sm" decorative />
      {collapsed ? null : (
        <>
          <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
            <Text as="span" size="xs" weight="semibold" tone="strong" truncate>
              {visible}
            </Text>
            <Text as="span" size="xs" tone="muted" truncate>
              {local ? "Sin usuario" : nombre}
            </Text>
          </span>
          {local ? null : <IconButton icon="log-out" label="Cerrar sesión" variant="ghost" size="sm" onClick={() => void salir()} />}
        </>
      )}
    </div>
  );
}

/* ⌘K: navegar a una pantalla o buscar una inmobiliaria por nombre, email o dominio. */
function Buscador({ abierto, onAbierto }: { abierto: boolean; onAbierto: (v: boolean) => void }) {
  const [consulta, setConsulta] = useState("");
  const [resultados, setResultados] = useState<ListaLeads["filas"]>([]);
  const [cargando, setCargando] = useState(false);
  const ultima = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = consulta.trim();
    ultima.current?.abort();
    if (q.length < 2) {
      setResultados([]);
      setCargando(false);
      return;
    }
    const ctrl = new AbortController();
    ultima.current = ctrl;
    setCargando(true);
    const t = setTimeout(() => {
      api<ListaLeads>(`/api/leads?q=${encodeURIComponent(q)}&porPagina=10`, { signal: ctrl.signal })
        .then((r) => setResultados(r.filas))
        .catch(() => undefined)
        .finally(() => !ctrl.signal.aborted && setCargando(false));
    }, 180);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [consulta]);

  const items = useMemo(
    () => [
      ...resultados.map((l) => ({
        id: `lead-${l.id}`,
        label: l.nombre,
        group: "Inmobiliarias",
        keywords: [consulta, l.email ?? "", l.dominio ?? "", l.zona ?? ""],
        icon: "building-2" as IconName,
        badge: l.zona ?? undefined,
        onSelect: () => navegar(`/leads/${l.id}`),
      })),
      ...[...ITEMS, ...AJUSTES].map((i) => ({ id: i.href, label: i.label, group: "Ir a", icon: i.icon, onSelect: () => navegar(i.href) })),
    ],
    [resultados, consulta],
  );

  return (
    <CommandPalette
      open={abierto}
      onOpenChange={(v) => {
        onAbierto(v);
        if (!v) setConsulta("");
      }}
      items={items}
      placeholder="Buscar inmobiliaria o pantalla"
      emptyMessage={consulta.trim().length < 2 ? "Escribí al menos dos letras." : "No hay inmobiliarias con ese nombre."}
      onQueryChange={setConsulta}
      loading={cargando}
    />
  );
}
